import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogIn, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";

const formatErr = (d) => {
  if (!d) return "Erreur de connexion";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || "Erreur";
  return d?.msg || "Erreur";
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Connexion réussie");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#F9F8F6" }}
    >
      <div className="w-full max-w-md">
        {/* Branding */}
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-yacoclean.jpg" alt="YACOCLEAN" data-testid="login-logo" className="w-24 h-24 rounded-2xl object-cover mb-4 shadow-sm" />
          <h1 className="font-heading text-3xl font-bold tracking-tight">YACOCLEAN</h1>
          <p className="text-sm text-[#5C665E] mt-1">Suivi de factures</p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl border bg-white p-8"
          style={{ borderColor: "#E5E2DC" }}
        >
          <h2 className="font-heading text-xl font-semibold mb-1">Connexion</h2>
          <p className="text-sm text-[#5C665E] mb-6">Accédez à votre espace de suivi.</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label
                htmlFor="email"
                className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block"
              >
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@entreprise.fr"
                className="bg-white border-[#E5E2DC] h-11"
              />
            </div>

            <div>
              <Label
                htmlFor="password"
                className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block"
              >
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white border-[#E5E2DC] h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C665E] hover:text-[#1A1D1A]"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full rounded-full py-3 text-sm font-semibold text-white btn-lift focus-brand flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: "#2C5545" }}
            >
              <LogIn size={16} />
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm text-[#2C5545] font-semibold hover:underline"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          <p className="text-[11px] text-[#5C665E] mt-6 text-center">
            Accès protégé · Session valide 30 jours
          </p>
        </div>
      </div>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
